import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import Pagination from './common/Pagination';
import usePagination from './common/usePagination';
import './PredictiveMonitoringSection.css';

/**
 * PredictiveMonitoringSection
 * ===========================
 * AI-powered predictive failure monitoring shown on every team dashboard.
 *
 * A weekly backend job (predictions_collector_lambda) reads each onboarded AWS
 * account's real CloudWatch metrics, alarm activity AND resource configuration
 * and asks AWS Bedrock (Claude) to predict the failures most likely in the NEXT
 * 7 DAYS.
 *
 * Predictions are ENVIRONMENT-SCOPED to this deployment: the dev dashboard
 * shows dev-account predictions and the prod dashboard shows prod-account
 * predictions. Because the environment is fixed per deployment, the list is a
 * single colour-flagged, paginated table with a REGION column (no environment
 * picker, no per-account sections) - consistent with the Idle Resources UX.
 */

const API_URL = getApiUrl();

const PAGE_SIZE = 10;

// Risk vocabulary + colour flags (worst -> best). ``rank`` drives sorting and
// the per-severity summary chips.
const RISK_META = {
  critical: { label: 'Critical', cls: 'pm-risk-critical', icon: '\uD83D\uDD34', rank: 0 },  // 🔴
  high: { label: 'High', cls: 'pm-risk-high', icon: '\uD83D\uDFE0', rank: 1 },              // 🟠
  medium: { label: 'Medium', cls: 'pm-risk-medium', icon: '\uD83D\uDFE1', rank: 2 },        // 🟡
  low: { label: 'Low', cls: 'pm-risk-low', icon: '\uD83D\uDFE2', rank: 3 },                 // 🟢
  pending: { label: 'Awaiting analysis', cls: 'pm-risk-pending', icon: '\u2014', rank: 9 },
};

const riskKey = (risk) => {
  const k = (risk || '').toLowerCase();
  return RISK_META[k] ? k : 'pending';
};
const riskMeta = (risk) => RISK_META[riskKey(risk)];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Format a YYYY-MM-DD day key as e.g. "7 Sep 2026".
const formatDay = (ymd) => {
  if (!ymd) return '';
  const parts = ymd.split('-');
  if (parts.length !== 3) return ymd;
  const [y, m, d] = parts;
  const idx = parseInt(m, 10) - 1;
  return idx >= 0 && idx < 12 ? `${parseInt(d, 10)} ${MONTHS[idx]} ${y}` : ymd;
};

// Severity order used for the summary chips + the "All" filter default sort.
const RISK_ORDER = ['critical', 'high', 'medium', 'low'];

const PredictiveMonitoringSection = ({ team, environment }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Risk filter: '' = all, or one of RISK_ORDER.
  const [riskFilter, setRiskFilter] = useState('');
  // Row expanded in the table to show full Signal / Impact / Recommendation.
  const [expandedRow, setExpandedRow] = useState(null);

  const fetchPredictions = useCallback(async () => {
    if (!team) return;
    setLoading(true);
    setError(null);
    try {
      const axios = (await import('axios')).default;
      // Env-scoped: the selected DEV/PROD environment picks that account's
      // predictions file so dev and prod are distinct.
      const res = await axios.get(`${API_URL}${API_ENDPOINTS.predictions(team, environment)}`);
      setData(res.data || null);
    } catch (err) {
      console.error('Error fetching predictions:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load predictions');
    } finally {
      setLoading(false);
    }
  }, [team, environment]);

  useEffect(() => {
    fetchPredictions();
  }, [fetchPredictions]);

  const current = data?.current || null;

  // Flat prediction list for this deployment's environment. Older stored data
  // may carry a per-account `accounts` breakdown; flatten it so the single
  // table works regardless of which shape was stored.
  const predictions = useMemo(() => {
    if (Array.isArray(current?.predictions) && current.predictions.length) {
      return current.predictions;
    }
    if (Array.isArray(current?.accounts)) {
      return current.accounts.flatMap((g) => (Array.isArray(g.predictions) ? g.predictions : []));
    }
    return [];
  }, [current]);

  const overall = current?.overallRisk || 'pending';
  const overallMeta = riskMeta(overall);

  // Number of account/region snapshots ACTUALLY represented in the data shown.
  // Prefer counting the distinct (environment, region) pairs present in the
  // predictions themselves so the figure always matches this environment's
  // rows - the stored `accountsAnalyzed` can be a stale pre-env-scoping value
  // (e.g. 4 = all envs x regions) that no longer reflects the env-scoped view.
  const snapshotsAnalyzed = useMemo(() => {
    const pairs = new Set();
    for (const p of predictions) {
      const env = (p.environment || '').trim();
      const region = (p.region || '').trim();
      if (env || region) pairs.add(`${env}|${region}`);
    }
    if (pairs.size > 0) return pairs.size;
    // Fall back to the stored count only when rows carry no env/region.
    return typeof current?.accountsAnalyzed === 'number' ? current.accountsAnalyzed : null;
  }, [predictions, current]);

  // Per-severity counts for the summary chips.
  const counts = useMemo(() => {
    const c = { critical: 0, high: 0, medium: 0, low: 0 };
    for (const p of predictions) {
      const k = riskKey(p.risk);
      if (k in c) c[k] += 1;
    }
    return c;
  }, [predictions]);

  // Sorted (worst first, then highest confidence) + risk-filtered.
  const sorted = useMemo(() => {
    const arr = [...predictions].sort((a, b) => {
      const ra = riskMeta(a.risk).rank;
      const rb = riskMeta(b.risk).rank;
      if (ra !== rb) return ra - rb;
      return (b.confidence || 0) - (a.confidence || 0);
    });
    if (!riskFilter) return arr;
    return arr.filter((p) => riskKey(p.risk) === riskFilter);
  }, [predictions, riskFilter]);

  const { page, setPage, pageCount, pageItems, totalItems, pageSize } =
    usePagination(sorted, PAGE_SIZE, [riskFilter, current?.weekOf]);

  const toggleRow = (idx) => setExpandedRow((prev) => (prev === idx ? null : idx));

  const renderChips = () => (
    <div className="pm-summary-chips">
      <button
        type="button"
        className={`pm-chip pm-chip-all ${riskFilter === '' ? 'active' : ''}`}
        onClick={() => setRiskFilter('')}
      >
        All <span className="pm-chip-count">{predictions.length}</span>
      </button>
      {RISK_ORDER.map((k) => (
        <button
          key={k}
          type="button"
          className={`pm-chip ${RISK_META[k].cls} ${riskFilter === k ? 'active' : ''} ${counts[k] === 0 ? 'empty' : ''}`}
          onClick={() => setRiskFilter(riskFilter === k ? '' : k)}
          disabled={counts[k] === 0}
        >
          {RISK_META[k].icon} {RISK_META[k].label} <span className="pm-chip-count">{counts[k]}</span>
        </button>
      ))}
    </div>
  );

  return (
    <section className="section-container pm-section">
      <h3 className="section-title">
        🔮 Predictive Monitoring
        <span className={`pm-overall-badge ${overallMeta.cls}`} title="Highest predicted risk this week">
          {overallMeta.icon} {overallMeta.label}
        </span>
      </h3>

      <p className="section-description">
        AI-predicted failures for the next 7 days for {team}, generated weekly by
        AWS Bedrock from this environment&apos;s real CloudWatch metrics, alarm
        activity &amp; resource configuration. Region is shown per row.
        {current?.weekOf && <> Week of <strong>{formatDay(current.weekOf)}</strong>.</>}
        {typeof snapshotsAnalyzed === 'number' && snapshotsAnalyzed > 0 && (
          <> Analyzed {snapshotsAnalyzed} region snapshot{snapshotsAnalyzed === 1 ? '' : 's'}.</>
        )}
      </p>

      {loading && !data ? (
        <div className="pm-loading"><div className="spinner-small" /><span>Loading predictions…</span></div>
      ) : error && !data ? (
        <div className="pm-error">
          <span>⚠️ {error}</span>
          <button onClick={fetchPredictions}>Retry</button>
        </div>
      ) : !current ? (
        <div className="pm-empty">
          <span className="pm-empty-icon">🔮</span>
          <p>No predictions yet. The weekly analysis runs every Monday; results appear here once available.</p>
        </div>
      ) : predictions.length === 0 ? (
        <div className="pm-empty pm-healthy">
          <span className="pm-empty-icon">✅</span>
          <p>No failures predicted for the coming week based on current metrics, alarms &amp; configuration.</p>
        </div>
      ) : (
        <>
          {/* Toolbar: severity chips */}
          <div className="pm-toolbar">
            {renderChips()}
          </div>

          <div className="pm-table-container">
            <table className="pm-table">
              <thead>
                <tr>
                  <th className="pm-col-risk">Risk</th>
                  <th>Predicted failure</th>
                  <th className="pm-col-service">Service</th>
                  <th className="pm-col-scope">Region</th>
                  <th className="pm-col-conf">Confidence</th>
                  <th className="pm-col-expand" aria-label="Details" />
                </tr>
              </thead>
              <tbody>
                {pageItems.map((p, i) => {
                  const idx = (page - 1) * pageSize + i;
                  const meta = riskMeta(p.risk);
                  const isOpen = expandedRow === idx;
                  return (
                    <React.Fragment key={`${p.title}-${idx}`}>
                      <tr
                        className={`pm-row ${meta.cls} ${isOpen ? 'expanded' : ''}`}
                        onClick={() => toggleRow(idx)}
                      >
                        <td className="pm-col-risk">
                          <span className={`pm-risk-flag ${meta.cls}`}>
                            {meta.icon} {meta.label}
                          </span>
                        </td>
                        <td className="pm-title-cell">{p.title}</td>
                        <td className="pm-col-service">{p.service || '—'}</td>
                        <td className="pm-col-scope">
                          {p.region ? <span className="pm-tag">{p.region}</span> : '—'}
                        </td>
                        <td className="pm-col-conf">
                          {typeof p.confidence === 'number' ? (
                            <span className="pm-conf-wrap" title="Model confidence">
                              <span className="pm-conf-bar">
                                <span className={`pm-conf-fill ${meta.cls}`} style={{ width: `${Math.max(0, Math.min(100, p.confidence))}%` }} />
                              </span>
                              <span className="pm-conf-num">{p.confidence}%</span>
                            </span>
                          ) : '—'}
                        </td>
                        <td className="pm-col-expand">
                          <span className={`pm-expand-caret ${isOpen ? 'open' : ''}`}>▸</span>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="pm-detail-row">
                          <td colSpan={6}>
                            <div className="pm-detail">
                              {p.signal && <p className="pm-line"><strong>Signal:</strong> {p.signal}</p>}
                              {p.impact && <p className="pm-line"><strong>Impact:</strong> {p.impact}</p>}
                              {p.recommendation && (
                                <p className="pm-line pm-reco"><strong>Recommended action:</strong> {p.recommendation}</p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            pageCount={pageCount}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setPage}
          />
        </>
      )}

      {data?.modelId && (
        <p className="pm-footnote">Predictions generated by {data.modelId} (AWS Bedrock).</p>
      )}
    </section>
  );
};

export default PredictiveMonitoringSection;
